<template>
  <el-card
    v-if="repository"
    v-loading="repository.state === 'creating'"
    id="build-card"
    v-bind:class="{ 'no-padding': !!build }"
    shadow="hover"
  >
    <div slot="header">
      <el-row :gutter="10">
        <el-col :span="12">
          <h4>
            <Repository :repository-id="repository.id" />
          </h4>
        </el-col>

        <el-col :span="12" align="right">
          <el-button
            type="text"
            class="commits"
            icon="mdi mdi-source-commit"
            size="mini"
          >
            {{ repository.commits }} {{ plural('commit', repository.commits) }}
          </el-button>

          <el-button
            v-if="applicationId"
            type="danger"
            size="mini"
            plain
            @click.prevent="unlink"
          >
            Unlink
          </el-button>

          <el-button
            v-else
            type="text"
            icon="mdi mdi-link-variant"
            size="mini"
          >
            13 applications
          </el-button>

          <el-dropdown
            v-if="instance"
            trigger="click"
            :hide-on-click="false"
            @command="action"
          >
            <el-button size="mini">
              Clone<i class="el-icon-arrow-down el-icon--right" />
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item id="repository-clone">
                <el-input :value="clone" size="mini" readonly>
                  <Clipboard
                    slot="append"
                    size="mini"
                    :value="clone"
                    button
                  />
                </el-input>
              </el-dropdown-item>

              <el-dropdown-item divided disabled>
                Transfer to another server
              </el-dropdown-item>

              <el-dropdown-item class="remove" command="remove">
                Remove local repository
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-col>
      </el-row>
    </div>

    <template v-if="!user.ssh && repository.instanceId">
      <el-alert
        type="warning"
        effect="dark"
        title="To clone and access a runstack Git you must upload or create a ssh key pair"
        :closable="false"
      >
        <el-button
          type="danger"
          icon="mdi mdi-key-variant"
          size="mini"
          plain
          @click="showGenerate = true"
        >
          Create a key
        </el-button>

        <router-link size="mini" to="/account">Upload a personal key</router-link>
      </el-alert>

      <AccountGenerateKey :visible.sync="showGenerate" />
    </template>

    <Build
      v-if="build"
      :build="build"
      :repository="repository"
      show-logs
    />

    <div v-else class="nothing">
      No builds for this repository.
      <el-button type="text" size="small">Read the documentation</el-button>
    </div>

    <ApplicationButton
      v-for="application in []"
      :key="application.id"
      :application-id="application.id"
      size="mini"
    />
  </el-card>
</template>

<script>
import { mapState } from 'vuex'
import Build from './Build'
import Repository from './Repository'
import Clipboard from '../Clipboard'
import ApplicationButton from '../Application/Button'
import AccountGenerateKey from '../Account/GenerateKey'

export default {
  props: {
    repository: Object,
    applicationId: String
  },
  components: {
    Build,
    Repository,
    Clipboard,
    ApplicationButton,
    AccountGenerateKey
  },
  data () {
    return {
      showGenerate: false
    }
  },
  computed: {
    clone () {
      return 'git clone git@' + this.instance.hostname + ':' + this.repository.name
    },
    instance () {
      return this.instances.find((i) => i.id === this.repository.instanceId)
    },
    build () {
      return this.builds.filter((b) => b.repositoryId === this.repository.id)
        .sort((a, b) => (a.created > b.created) ? 1 : -1)
        .pop()
    },
    ...mapState(['instances', 'builds', 'applications', 'user'])
  },
  methods: {
    action (command) {
      this[command]()
    },
    unlink () {
      this.$confirm('Unlinking repository will causes builds to not work with this application. Continue?', 'Warning', {
        confirmButtonText: 'Unlink',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--danger'
      }).then(() => this.$store.dispatch('unlinkRepository', {
        ...this.repository,
        applicationId: this.applicationId
      })).then(() => this.$emit('change'))
        .catch(() => {})
    },
    remove () {
      const msg = 'Once you remove, there is no going back. To confirm, please enter REMOVE below.'
      this.$prompt(msg, 'All data and builds will be lost !', {
        confirmButtonText: 'Remove Repository',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--danger',
        inputValidator: (value) => value === 'REMOVE',
        inputErrorMessage: 'Invalid input'
      }).then(() => this.$store.dispatch('removeRepository', this.repository))
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss">
  #build-card {
    .el-card__header {
      padding: 9px 20px;
    }

    &.no-padding {
      .el-card__body {
        padding: 0;
      }
    }

    .el-button [class*="mdi-"] + span {
      margin-left: 0;
    }
  }
  #repository-clone {
    min-width: 400px;
  }
  .el-dropdown-menu__item {
    .el-input-group__append {
      padding: 0 10px 0 13px;
    }
  }
</style>

<style lang="scss" scoped>
  @import "../../theme";

  .el-card {
    margin-bottom: 15px;

    h4 {
      margin: 0;
      line-height: 28px;

      .el-button {
        padding: 0;
      }
    }

    .nothing {
      line-height: 28px;
      font-size: 12px;
    }

    .el-dropdown {
      margin-left: 15px;
    }
  }

  .remove {
    color: $--color-danger;
  }

  .el-alert {
    margin: -12px -12px 15px;
    width: calc(100% + 24px);
    border-radius: 0;

    a {
      color: #ffffff;
      text-decoration: none;
      margin-left: 10px;
    }
    .el-button--danger:hover {
      background: #ffffff;
      color: $--color-danger;
      border-color: $--color-danger;
    }
  }

  .commits {
    color: $--color-text-primary;
  }
</style>
